import type { ErrorEvent as SentryErrorEvent } from '@sentry/angular'

/**
 * Keeps the browser source when Angular wraps an ErrorEvent without an exception.
 * Filters only the browser's ResizeObserver notification warnings.
 * @param {SentryErrorEvent} event
 * @param {unknown} originalException
 * @returns {SentryErrorEvent | null}
 */
export function prepareBrowserErrorEvent(event: SentryErrorEvent, originalException: unknown): SentryErrorEvent | null {
  if (
    !(originalException instanceof Error) ||
    typeof ErrorEvent === 'undefined' ||
    !(originalException.cause instanceof ErrorEvent) ||
    originalException.cause.error
  ) {
    return event
  }

  const cause = originalException.cause

  if (
    cause.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    cause.message === 'ResizeObserver loop limit exceeded'
  ) {
    return null
  }

  const filename = sanitizeScriptSource(cause.filename)

  const result: SentryErrorEvent = {
    ...event,
    contexts: {
      ...event.contexts,
      browser_error: {
        message: cause.message,
        filename,
        lineno: cause.lineno,
        colno: cause.colno
      }
    },
    // The wrapper stack is identical for unrelated browser errors. Do not group by it.
    fingerprint: ['browser-error-event', cause.message, filename || '<unknown>']
  }

  const values = event.exception?.values

  if (filename && values?.length) {
    result.exception = {
      ...event.exception,
      values: values.map((exception, index) => {
        if (index !== values.length - 1) {
          return exception
        }

        return {
          ...exception,
          // A primitive throw has no Error stack. Use only the source reported by the browser.
          stacktrace: {
            frames: [{ filename, lineno: cause.lineno || undefined, colno: cause.colno || undefined }]
          },
          mechanism: { ...exception.mechanism, type: exception.mechanism?.type ?? 'generic', synthetic: true }
        }
      })
    }
  }

  return result
}

/**
 * Removes credentials and URL parameters from a script source before reporting it.
 * @param {string} filename
 * @returns {string}
 */
function sanitizeScriptSource(filename: string): string {
  try {
    const url = new URL(filename)

    url.username = ''
    url.password = ''
    url.search = ''
    url.hash = ''

    return url.href
  } catch {
    return filename.split(/[?#]/)[0]
  }
}
